import dayjs from 'dayjs';
import type {
    RecordedPaymentType,
    SalesTransaction,
    SalesTransactionDiscount,
} from '@/api/models';
import { formatDiscountValue, formatGel } from '@/utils';

/**
 * Pure presentational config + helpers for the transactions page. No React, no
 * state — the date default, the per-day page size, and the row-rendering maps
 * (payment icon/colour/label, status badge) the render-only views read. Mirrors
 * the bos-app Sales transactions widget's meta, trimmed to what this page needs.
 */

/** Today as an ISO `YYYY-MM-DD` string in the browser's local zone. */
export const todayIso = (): string => dayjs().format('YYYY-MM-DD');

/**
 * Default rows-per-page for the transactions table. A single day can hold
 * thousands of sales, so the table pages server-side (the bos-api caps
 * `pageSize` at 200).
 */
export const TRANSACTIONS_PAGE_SIZE = 25;

/** Selectable rows-per-page sizes (mirrors the bos-app tables). */
export const TRANSACTIONS_PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 200];

/**
 * Page numbers to render in the pagination control, collapsing long ranges with
 * `'ellipsis'` markers (first/last always shown, plus a window around the
 * current page). Pure — unit-tested directly. Ported verbatim from the bos-app
 * tables so the two paginate identically.
 */
export const getPaginationWindow = (
    totalPages: number,
    current: number,
): (number | 'ellipsis')[] => {
    const pages: (number | 'ellipsis')[] = [];
    if (totalPages <= 7) {
        for (let i = 1; i <= totalPages; i += 1) {
            pages.push(i);
        }
        return pages;
    }
    pages.push(1);
    if (current > 3) {
        pages.push('ellipsis');
    }
    const start = Math.max(2, current - 1);
    const end = Math.min(totalPages - 1, current + 1);
    for (let i = start; i <= end; i += 1) {
        pages.push(i);
    }
    if (current < totalPages - 2) {
        pages.push('ellipsis');
    }
    pages.push(totalPages);
    return pages;
};

/** Total units sold on a transaction — sum of `quantity` across cart lines. */
export const getCartUnitsCount = (transaction: SalesTransaction): number =>
    transaction.cart.reduce((sum, item) => sum + item.quantity, 0);

/** What a row's payment cell names: one method, or `Combined` for a split. */
export type PaymentKind = RecordedPaymentType | 'Combined';

/**
 * The row's payment cell: the method the sale was paid with, or `Combined`
 * when it mixes methods (the till's name for a split; the detail lists each
 * leg). Naming only the larger leg hid the cash in a cash + card sale
 * (QA #100). `null` when there are no payments.
 */
export const getPaymentKind = (
    transaction: SalesTransaction,
): PaymentKind | null => {
    const types = new Set(transaction.payments.map((payment) => payment.type));
    if (types.size > 1) {
        return 'Combined';
    }
    const [only] = types;
    return only ?? null;
};

/** Short, cashier-recognisable transaction id (passthrough for now). */
export const formatTransactionId = (id: string): string => id;

/** Time-of-day (`HH:mm`) the transaction was booked. */
export const formatTransactionTime = (transaction: SalesTransaction): string =>
    dayjs(transaction.transactionDate).format('HH:mm');

export type PaymentMeta = { labelKey: string; icon: string; color: string };

/** Payment kind → label key (pages ns) + icon + accent colour class. */
export const PAYMENT_META: Record<PaymentKind, PaymentMeta> = {
    Cash: {
        labelKey: 'transactions.payments.cash',
        icon: 'solar:wad-of-money-bold-duotone',
        color: 'text-success',
    },
    Card: {
        labelKey: 'transactions.payments.card',
        icon: 'solar:card-bold-duotone',
        color: 'text-accent',
    },
    Invoice: {
        labelKey: 'transactions.payments.invoice',
        icon: 'solar:bill-list-bold-duotone',
        color: 'text-warning',
    },
    Voucher: {
        labelKey: 'transactions.payments.voucher',
        icon: 'solar:ticket-bold-duotone',
        color: 'text-muted',
    },
    Combined: {
        labelKey: 'transactions.payments.combined',
        icon: 'solar:layers-minimalistic-bold-duotone',
        color: 'text-muted',
    },
};

/**
 * Row actions surfaced in the grid's actions dropdown. Today the detail modal
 * is read-only so `details` is the sole action; the array shape leaves room for
 * mutating actions later without touching the view (mirrors the bos-app widget).
 */
export type TransactionRowAction = 'details';

export const TRANSACTION_ROW_ACTIONS: {
    id: TransactionRowAction;
    icon: string;
    labelKey: string;
}[] = [
    {
        id: 'details',
        icon: 'solar:eye-bold-duotone',
        labelKey: 'transactions.actions.details',
    },
];

export type StatusBadgeKind = 'done' | 'refund' | 'pending' | 'canceled';

/**
 * Resolves a transaction's status (and refund presence) to a single badge
 * kind, so the view renders without per-row branching. A refunded transaction
 * always reads "refund" regardless of its underlying status.
 */
export const getStatusBadgeKind = (
    transaction: SalesTransaction,
): StatusBadgeKind => {
    if (transaction.refund) {
        return 'refund';
    }
    switch (transaction.status) {
        case 'Done':
            return 'done';
        case 'Canceled':
            return 'canceled';
        default:
            return 'pending';
    }
};

/** Badge kind → label key (pages ns) + soft chip colour. */
export const STATUS_BADGE_META: Record<
    StatusBadgeKind,
    { labelKey: string; color: 'success' | 'warning' | 'danger' | 'default' }
> = {
    done: { labelKey: 'transactions.status.done', color: 'success' },
    pending: { labelKey: 'transactions.status.pending', color: 'warning' },
    refund: { labelKey: 'transactions.status.refund', color: 'danger' },
    canceled: { labelKey: 'transactions.status.canceled', color: 'default' },
};

/** The sale was discounted, on a line or as a whole (SEL-100). */
export const isDiscounted = (transaction: SalesTransaction): boolean =>
    transaction.discountTotal > 0;

/** A line discount's tag, as entered and taken off: `−10%`, `−₾ 5.00`. */
export const lineDiscountTag = (discount: SalesTransactionDiscount): string =>
    `−${formatDiscountValue(discount)}`;

/**
 * The sale discount's tag: a percentage as entered (`10%`, its amount sits
 * beside it), a fixed one as the money taken off (`−₾ 5.00`).
 */
export const saleDiscountTag = (discount: SalesTransactionDiscount): string =>
    discount.type === 'percentage'
        ? formatDiscountValue(discount)
        : `−${formatGel(discount.value)}`;
